import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Table,
  Modal,
  Button,
  Form,
  Alert,
  Spinner,
  Badge,
  InputGroup,
  Card,
  Dropdown,
  Pagination,
  Container,
  Row,
  Col,
  Image,
  ListGroup,
} from "react-bootstrap";
import {
  FaTrashAlt,
  FaEdit,
  FaEye,
  FaPlus,
  FaSearch,
  FaEllipsisV,
  FaUserCircle,
  FaFilter,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaUserTie,
  FaUsers,
  FaCalendarAlt,
} from "react-icons/fa";
import { APICall, APICallWithImage } from "../../Utls/Api";
import { useSelector } from "react-redux";

// Custom Avatar Component
const Avatar = ({ name, size = 40, className = "", src }) => {
  const initials = name
    ? name
        .split(" ")
        .map((n) => n[0])
        .join("")
    : "";

  return src ? (
    <Image
      src={src}
      roundedCircle
      className={`object-fit-cover ${className}`}
      style={{
        width: size,
        height: size,
      }}
    />
  ) : (
    <div
      className={`rounded-circle bg-primary text-white d-flex align-items-center justify-content-center ${className}`}
      style={{
        width: size,
        height: size,
        fontSize: size * 0.4,
        fontWeight: "bold",
      }}
    >
      {initials}
    </div>
  );
};

const SupervisorHero = () => {
  // State Management
  const [modals, setModals] = useState({
    add: false,
    edit: false,
    delete: false,
    details: false,
    baseline: false,
  });
  const [supervisors, setSupervisors] = useState([]);
  const [managers, setManagers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const user = useSelector((state) => state.user.user);

  const [alert, setAlert] = useState({
    show: false,
    message: "",
    variant: "success",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [supervisorData, setSupervisorData] = useState({
    fullname: "",
    email: "",
    password: "",
    managerid: "",
    department: "",
    file: null,
  });
  const [supervisorToDelete, setSupervisorToDelete] = useState(null);
  const [supervisorToEdit, setSupervisorToEdit] = useState(null);
  const [selectedSupervisor, setSelectedSupervisor] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: "fullname",
    direction: "asc",
  });
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(5);
  const [baseline, setBaseline] = useState({
    low: { min: 0, max: 4 },
    regular: { min: 5, max: 8 },
    medium: { min: 9, max: 14 },
    high: { min: 15, max: 20 },
  });

  // Helper Functions
  const showAlert = useCallback((message, variant = "success") => {
    setAlert({ show: true, message, variant });
    setTimeout(() => setAlert((a) => ({ ...a, show: false })), 3000);
  }, []);

  const toggleModal = useCallback((modalName, value) => {
    setModals((prev) => ({ ...prev, [modalName]: value }));
  }, []);

  // Data Fetching
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [managersRes, supervisorsRes, departmentsRes] = await Promise.all([
        APICall("auth/manager/all", "GET"),
        APICall("auth/supervisor/all", "GET"),
        APICall("auth/subdepartments", "GET"),
      ]);

      console.log("subdepartmentsRes:", departmentsRes);

      setManagers(managersRes.managers || []);
      setSupervisors(supervisorsRes.supervisors || []);
      setDepartments(departmentsRes.data || []);
    } catch (error) {
      showAlert("Failed to fetch data", "danger");
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [showAlert]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Table Sorting and Filtering
  const sortedSupervisors = useMemo(() => {
    const sortableItems = [...supervisors];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        const aValue = a[sortConfig.key] || "";
        const bValue = b[sortConfig.key] || "";
        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [supervisors, sortConfig]);

  const filteredSupervisors = useMemo(() => {
    return sortedSupervisors.filter(
      (supervisor) =>
        supervisor.fullname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        supervisor.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [sortedSupervisors, searchTerm]);

  // Pagination
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredSupervisors.slice(
    indexOfFirstRow,
    indexOfLastRow
  );
  const totalPages = Math.ceil(filteredSupervisors.length / rowsPerPage);

  const requestSort = useCallback(
    (key) => {
      const direction =
        sortConfig.key === key && sortConfig.direction === "asc"
          ? "desc"
          : "asc";
      setSortConfig({ key, direction });
    },
    [sortConfig]
  );

  const getSortIcon = useCallback(
    (key) => {
      if (sortConfig.key !== key) return <FaSort className="ms-1" />;
      return sortConfig.direction === "asc" ? (
        <FaSortUp className="ms-1" />
      ) : (
        <FaSortDown className="ms-1" />
      );
    },
    [sortConfig]
  );

  // Row Selection
  const handleSelectRow = useCallback((id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  }, []);

  const handleSelectAllRows = useCallback(
    (e) => {
      setSelectedRows(
        e.target.checked ? currentRows.map((row) => row._id) : []
      );
    },
    [currentRows]
  );

  // Supervisor Actions
  const handleDeleteClick = useCallback(
    (supervisor) => {
      setSupervisorToDelete(supervisor);
      toggleModal("delete", true);
    },
    [toggleModal]
  );

  const handleEditClick = useCallback(
    (supervisor) => {
      setSupervisorToEdit(supervisor);
      setSupervisorData({
        fullname: supervisor.fullname,
        email: supervisor.email,
        password: "",
        managerid: supervisor.userid || "",
        department: supervisor.department || "",
        file: null,
      });
      toggleModal("edit", true);
    },
    [toggleModal]
  );

  const handleViewDetails = useCallback(
    (supervisor) => {
      setSelectedSupervisor(supervisor);
      toggleModal("details", true);
    },
    [toggleModal]
  );

  const handleDelete = useCallback(async () => {
    try {
      setLoading(true);
      await APICall(`auth/supervisor/delete`, "POST", {
        id: supervisorToDelete._id,
      });
      showAlert("Supervisor deleted successfully");
      fetchData();
      setSelectedRows((prev) =>
        prev.filter((rowId) => rowId !== supervisorToDelete._id)
      );
      toggleModal("delete", false);
    } catch (error) {
      showAlert("Failed to delete supervisor", "danger");
      console.error("Error deleting supervisor:", error);
    } finally {
      setLoading(false);
    }
  }, [supervisorToDelete, showAlert, fetchData, toggleModal]);

  const handleBulkDelete = useCallback(() => {
    if (
      window.confirm(
        `Are you sure you want to delete ${selectedRows.length} supervisors?`
      )
    ) {
      // Implement bulk delete logic here
      showAlert(`${selectedRows.length} supervisors deleted`, "success");
      setSelectedRows([]);
    }
  }, [selectedRows.length, showAlert]);

  // Form Handlers
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setSupervisorData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleFileChange = useCallback((e) => {
    setSupervisorData((prev) => ({ ...prev, file: e.target.files[0] }));
  }, []);

  const handleSliderChange = useCallback((key, newMax) => {
    setBaseline((prev) => {
      const newBaseline = { ...prev };

      if (key === "low") {
        newBaseline.low.max = newMax;
        newBaseline.regular.min = newMax + 1;
        if (newBaseline.regular.max < newBaseline.regular.min)
          newBaseline.regular.max = newBaseline.regular.min;
        newBaseline.medium.min = newBaseline.regular.max + 1;
        if (newBaseline.medium.max < newBaseline.medium.min)
          newBaseline.medium.max = newBaseline.medium.min;
        newBaseline.high.min = newBaseline.medium.max + 1;
        newBaseline.high.max = 20;
      }

      if (key === "regular") {
        newBaseline.regular.max = newMax;
        newBaseline.medium.min = newMax + 1;
        if (newBaseline.medium.max < newBaseline.medium.min)
          newBaseline.medium.max = newBaseline.medium.min;
        newBaseline.high.min = newBaseline.medium.max + 1;
        newBaseline.high.max = 20;
      }

      if (key === "medium") {
        newBaseline.medium.max = newMax;
        newBaseline.high.min = newMax + 1;
        newBaseline.high.max = 20;
      }

      return newBaseline;
    });
  }, []);

  const handleSetBaseline = useCallback(async () => {
    try {
      const response = await APICall(
        "supervisor/setBaseline",
        "POST",
        baseline
      );
      if (response?.success === false) {
        throw new Error(response.message || "Error setting baseline");
      }
      showAlert("Baseline set successfully");
      toggleModal("baseline", false);
    } catch (err) {
      console.error("Baseline error:", err);
      showAlert(`Failed to set baseline: ${err.message}`, "danger");
    }
  }, [baseline, showAlert, toggleModal]);

  const handleAddSubmit = useCallback(async () => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("name", supervisorData.fullname);
      formData.append("email", supervisorData.email);
      formData.append("password", supervisorData.password);
      formData.append("managerId", user?.user_type !== "manager" ? supervisorData.managerid : user._id);
      formData.append("department", supervisorData.department);
      if (supervisorData.file) formData.append("file", supervisorData.file);

      await APICallWithImage("auth/supervisor/create", "POST", formData);

      showAlert("Supervisor created successfully");
      toggleModal("add", false);
      toggleModal("baseline", true);
      setSupervisorData({
        fullname: "",
        email: "",
        password: "",
        managerid: "",
        department: "",
        file: null,
      });
      fetchData();
    } catch (error) {
      showAlert(`Failed to create supervisor: ${error.message}`, "danger");
      console.error("Error creating supervisor:", error);
    } finally {
      setLoading(false);
    }
  }, [supervisorData, showAlert, fetchData, toggleModal]);

  const handleEditSubmit = useCallback(async () => {
    try {
      setLoading(true);
      console.log("u" , user)
      const formData = new FormData();
      formData.append("fullname", supervisorData.fullname);
      formData.append("email", supervisorData.email);
      formData.append(
        "managerId",
        user?.user_type !== "manager" ? supervisorData.managerid : user._id
      );
      formData.append("department", supervisorData.department);
      formData.append("id", supervisorToEdit._id);
      if (supervisorData.password)
        formData.append("password", supervisorData.password);
      if (supervisorData.file) formData.append("file", supervisorData.file);

      await APICallWithImage(`auth/supervisor/update`, "POST", formData);

      showAlert("Supervisor updated successfully");
      toggleModal("edit", false);
      setSupervisorData({
        fullname: "",
        email: "",
        password: "",
        managerid: "",
        department: "",
        file: null,
      });
      fetchData();
    } catch (error) {
      showAlert(`Failed to update supervisor: ${error.message}`, "danger");
      console.error("Error updating supervisor:", error);
    } finally {
      setLoading(false);
    }
  }, [supervisorData, supervisorToEdit, showAlert, fetchData, toggleModal]);

  return (
    <Container fluid className="p-3 p-md-4">
      {/* Alert Notification */}
      {alert.show && (
        <Alert
          variant={alert.variant}
          className="fade-in-alert"
          onClose={() => setAlert((a) => ({ ...a, show: false }))}
          dismissible
        >
          {alert.message}
        </Alert>
      )}

      {/* Header Section */}
      <Row className="mb-4">
        <Col xs={12} md={6} className="mb-3 mb-md-0">
          <h2 className="mb-1">Supervisor Management</h2>
          <p className="text-muted mb-0">
            Manage your supervisors and their permissions
          </p>
        </Col>
        <Col
          xs={12}
          md={6}
          className="d-flex justify-content-md-end align-items-md-center"
        >
          <div>
            <Button
              variant="primary"
              onClick={() => toggleModal("add", true)}
              disabled={loading}
            >
              <FaPlus className="me-2" />
              Add Supervisor
            </Button>
          </div>
        </Col>
      </Row>

      {/* Search and Filter Bar */}
      <Card className="mb-4 shadow-sm">
        <Card.Body className="p-3">
          <Row className="align-items-center">
            <Col xs={12} md={6} className="mb-3 mb-md-0">
              <InputGroup>
                <InputGroup.Text className="bg-white">
                  <FaSearch className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search supervisors..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="border-start-0"
                />
              </InputGroup>
            </Col>
            <Col xs={12} md={6} className="d-flex justify-content-md-end">
              <div className="d-flex align-items-center gap-2 w-100 w-md-auto">
                {selectedRows.length > 0 && (
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={handleBulkDelete}
                    className="d-flex align-items-center flex-grow-1 flex-md-grow-0 justify-content-center"
                  >
                    <FaTrashAlt className="me-1" />
                    Delete ({selectedRows.length})
                  </Button>
                )}
                <Dropdown className="flex-grow-1 flex-md-grow-0">
                  <Dropdown.Toggle
                    variant="outline-secondary"
                    className="d-flex align-items-center w-100"
                  >
                    <FaFilter className="me-2" />
                    Filters
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item>Active</Dropdown.Item>
                    <Dropdown.Item>Inactive</Dropdown.Item>
                    <Dropdown.Item>Recently Added</Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Supervisors Table */}
      <Card className="shadow-sm rounded-3">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Loading supervisors...</p>
            </div>
          ) : (
            <>
              <div className="table-responsive rounded-3 shadow-sm">
                <Table hover className="mb-0">
                  <thead className="table-dark">
                    <tr>
                      <th className="ps-3 ps-md-4" style={{ width: "50px" }}>
                        <Form.Check
                          type="checkbox"
                          onChange={handleSelectAllRows}
                          checked={
                            selectedRows.length === currentRows.length &&
                            currentRows.length > 0
                          }
                        />
                      </th>
                      <th
                        className="cursor-pointer"
                        onClick={() => requestSort("fullname")}
                      >
                        <div className="d-flex align-items-center">
                          Supervisor
                          {getSortIcon("fullname")}
                        </div>
                      </th>
                      <th
                        className="cursor-pointer d-none d-md-table-cell"
                        onClick={() => requestSort("email")}
                      >
                        <div className="d-flex align-items-center">
                          Email
                          {getSortIcon("email")}
                        </div>
                      </th>
                      <th className="d-none d-md-table-cell">Department</th>
                      <th className="d-none d-md-table-cell">Status</th>
                      <th className="d-none d-md-table-cell">Role</th>
                      <th className="text-end pe-3 pe-md-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentRows.length > 0 ? (
                      currentRows.map((supervisor) => (
                        <tr
                          key={supervisor._id}
                          className={
                            selectedRows.includes(supervisor._id)
                              ? "table-active"
                              : ""
                          }
                        >
                          <td className="ps-3 ps-md-4">
                            <Form.Check
                              type="checkbox"
                              checked={selectedRows.includes(supervisor._id)}
                              onChange={() => handleSelectRow(supervisor._id)}
                            />
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <Avatar
                                name={supervisor.fullname}
                                src={supervisor.avatar}
                                className="me-2 me-md-3"
                                size={32}
                              />
                              <div>
                                <div className="fw-semibold">
                                  {supervisor.fullname}
                                </div>
                                <small className="text-muted d-md-none">
                                  {supervisor.email}
                                </small>
                              </div>
                            </div>
                          </td>
                          <td className="d-none d-md-table-cell">
                            {supervisor.email}
                          </td>
                          <td className="d-none d-md-table-cell">
                            {supervisor.department || "N/A"}
                          </td>
                          <td className="d-none d-md-table-cell">
                            <Badge bg="success" pill>
                              Active
                            </Badge>
                          </td>
                          <td className="d-none d-md-table-cell">Supervisor</td>
                          <td className="text-end pe-3 pe-md-4">
                            <div className="d-flex justify-content-end gap-1 gap-md-2">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleViewDetails(supervisor)}
                                title="View Details"
                                className="p-1 p-md-2"
                              >
                                <FaEye />
                              </Button>
                              <Button
                                variant="outline-warning"
                                size="sm"
                                onClick={() => handleEditClick(supervisor)}
                                title="Edit"
                                className="p-1 p-md-2"
                              >
                                <FaEdit />
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDeleteClick(supervisor)}
                                title="Delete"
                                className="p-1 p-md-2"
                              >
                                <FaTrashAlt />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center py-5">
                          <FaUserCircle size={48} className="text-muted mb-3" />
                          <h5>No supervisors found</h5>
                          <p className="text-muted">
                            Try adjusting your search or add a new supervisor
                          </p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {filteredSupervisors.length > rowsPerPage && (
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center p-3 border-top">
                  <div className="text-muted mb-2 mb-md-0">
                    Showing {indexOfFirstRow + 1} to{" "}
                    {Math.min(indexOfLastRow, filteredSupervisors.length)} of{" "}
                    {filteredSupervisors.length} entries
                  </div>
                  <Pagination className="mb-0">
                    <Pagination.Prev
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                      }
                      disabled={currentPage === 1}
                    />
                    {Array.from({ length: totalPages }, (_, i) => (
                      <Pagination.Item
                        key={i + 1}
                        active={i + 1 === currentPage}
                        onClick={() => setCurrentPage(i + 1)}
                      >
                        {i + 1}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                      }
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Add Supervisor Modal */}
      <Modal
        show={modals.add}
        onHide={() => toggleModal("add", false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="h4">Add New Supervisor</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Full Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter full name"
                    name="fullname"
                    value={supervisorData.fullname}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email address</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Enter email"
                    name="email"
                    value={supervisorData.email}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Password</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Enter password"
                    name="password"
                    value={supervisorData.password}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </Form.Group>
              </Col>
              {user.user_type !== "manager" && (
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Manager</Form.Label>
                    <Form.Select
                      name="managerid"
                      value={supervisorData.managerid}
                      onChange={handleChange}
                      disabled={loading}
                    >
                      <option value="">Select a manager</option>
                      {managers.map((manager) => (
                        <option key={manager._id} value={manager._id}>
                          {manager.fullname}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              )}
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Profile Picture</Form.Label>
                  <Form.Control
                    type="file"
                    onChange={handleFileChange}
                    disabled={loading}
                    accept="image/*"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Sub Department</Form.Label>
                  <Form.Select
                    name="department"
                    value={supervisorData.department}
                    onChange={handleChange}
                    disabled={loading}
                  >
                    <option value="">Select a department</option>
                    {departments.map((dept) => (
                      <option key={dept._id} value={dept.name}>
                        {dept.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button
            variant="light"
            onClick={() => toggleModal("add", false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAddSubmit}
            disabled={
              loading ||
              !supervisorData.fullname ||
              !supervisorData.email ||
              !supervisorData.password ||
              // !supervisorData.managerid ||
              !supervisorData.department
            }
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  size="sm"
                  animation="border"
                  role="status"
                  className="me-2"
                />
                Adding...
              </>
            ) : (
              "Add Supervisor"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Supervisor Modal */}
      <Modal
        show={modals.edit}
        onHide={() => toggleModal("edit", false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="h4">Edit Supervisor</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Full Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter full name"
                    name="fullname"
                    value={supervisorData.fullname}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email address</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Enter email"
                    name="email"
                    value={supervisorData.email}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    New Password (leave blank to keep current)
                  </Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Enter new password"
                    name="password"
                    value={supervisorData.password}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Manager</Form.Label>
                  <Form.Select
                    name="managerid"
                    value={supervisorData.managerid}
                    onChange={handleChange}
                    disabled={loading}
                  >
                    <option value="">Select a manager</option>
                    {managers.map((manager) => (
                      <option key={manager._id} value={manager._id}>
                        {manager.fullname}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Profile Picture</Form.Label>
                  <Form.Control
                    type="file"
                    onChange={handleFileChange}
                    disabled={loading}
                    accept="image/*"
                  />
                  <Form.Text className="text-muted">
                    Current: {supervisorToEdit?.avatar ? "Uploaded" : "Default"}
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Sub Department</Form.Label>
                  <Form.Select
                    name="department"
                    value={supervisorData.department}
                    onChange={handleChange}
                    disabled={loading}
                  >
                    <option value="">Select a sub department</option>
                    {departments.map((dept) => (
                      <option key={dept._id} value={dept.name}>
                        {dept.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button
            variant="light"
            onClick={() => toggleModal("edit", false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleEditSubmit}
            disabled={
              loading ||
              !supervisorData.fullname ||
              !supervisorData.email ||
              !supervisorData.managerid ||
              !supervisorData.department
            }
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  size="sm"
                  animation="border"
                  role="status"
                  className="me-2"
                />
                Updating...
              </>
            ) : (
              "Update Supervisor"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={modals.delete}
        onHide={() => toggleModal("delete", false)}
        centered
      >
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="h5">Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete supervisor{" "}
            <strong>{supervisorToDelete?.fullname}</strong>?
          </p>
          <p className="text-muted">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button
            variant="light"
            onClick={() => toggleModal("delete", false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={loading}>
            {loading ? (
              <>
                <Spinner
                  as="span"
                  size="sm"
                  animation="border"
                  role="status"
                  className="me-2"
                />
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Supervisor Details Modal */}
      <Modal
        show={modals.details}
        onHide={() => toggleModal("details", false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="h4">Supervisor Details</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
          {selectedSupervisor && (
            <Row>
              <Col md={4} className="text-center mb-4 mb-md-0">
                <Avatar
                  name={selectedSupervisor.fullname}
                  src={selectedSupervisor.avatar}
                  size={120}
                  className="mb-3"
                />
                <h4>{selectedSupervisor.fullname}</h4>
                <Badge bg="primary" className="mb-3">
                  Supervisor
                </Badge>
                <ListGroup variant="flush" className="text-start">
                  <ListGroup.Item className="d-flex align-items-center">
                    <FaUserTie className="me-2 text-muted" />
                    <span>{selectedSupervisor.department || "N/A"}</span>
                  </ListGroup.Item>
                  <ListGroup.Item className="d-flex align-items-center">
                    <FaUsers className="me-2 text-muted" />
                    <span>
                      {selectedSupervisor.totalEmployees || 0} Employees
                    </span>
                  </ListGroup.Item>
                  <ListGroup.Item className="d-flex align-items-center">
                    <FaCalendarAlt className="me-2 text-muted" />
                    <span>
                      Joined{" "}
                      {new Date(
                        selectedSupervisor.createdAt
                      ).toLocaleDateString()}
                    </span>
                  </ListGroup.Item>
                </ListGroup>
              </Col>
              <Col md={8}>
                <Card>
                  <Card.Body>
                    <h5 className="mb-3">Contact Information</h5>
                    <ListGroup variant="flush">
                      <ListGroup.Item>
                        <strong>Email:</strong> {selectedSupervisor.email}
                      </ListGroup.Item>
                      <ListGroup.Item>
                        <strong>Manager ID:</strong>{" "}
                        {selectedSupervisor.userid || "N/A"}
                      </ListGroup.Item>
                      <ListGroup.Item>
                        <strong>Status:</strong>{" "}
                        <Badge bg="success">Active</Badge>
                      </ListGroup.Item>
                      <ListGroup.Item>
                        <strong>Last Updated:</strong>{" "}
                        {new Date(
                          selectedSupervisor.updatedAt
                        ).toLocaleString()}
                      </ListGroup.Item>
                      {selectedSupervisor.acitveAssignment && (
                        <ListGroup.Item>
                          <strong>Active Assignment:</strong>{" "}
                          {selectedSupervisor.acitveAssignment}
                        </ListGroup.Item>
                      )}
                    </ListGroup>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="light" onClick={() => toggleModal("details", false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Set Baseline Modal */}
      <Modal
        show={modals.baseline}
        onHide={() => toggleModal("baseline", false)}
        centered
        size="md"
      >
        <Modal.Header closeButton>
          <Modal.Title>Set Supervisor Baseline</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {["low", "regular", "medium"].map((key) => (
            <div key={key} className="mb-4">
              <div className="d-flex justify-content-between mb-1">
                <strong className="text-capitalize">{key}</strong>
                <span>
                  {baseline[key].min} - {baseline[key].max}
                </span>
              </div>
              <InputGroup>
                <Form.Range
                  min={baseline[key].min}
                  max={20}
                  value={baseline[key].max}
                  onChange={(e) =>
                    handleSliderChange(key, parseInt(e.target.value))
                  }
                />
                <Form.Control
                  type="number"
                  min={baseline[key].min}
                  max={20}
                  value={baseline[key].max}
                  onChange={(e) =>
                    handleSliderChange(key, parseInt(e.target.value))
                  }
                  style={{ width: 70 }}
                />
              </InputGroup>
            </div>
          ))}

          {/* High is auto-filled */}
          <div className="mb-3">
            <strong className="text-capitalize">High</strong>
            <p className="mt-2">
              {baseline.high.min} - {baseline.high.max}
            </p>
            <div className="bg-warning rounded" style={{ height: 10 }}></div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => toggleModal("baseline", false)}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSetBaseline}>
            Save Baseline
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Custom CSS for animations */}
      <style jsx>{`
        .fade-in-alert {
          animation: fadeIn 0.3s;
        }
        .cursor-pointer {
          cursor: pointer;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .table-responsive {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }
      `}</style>
    </Container>
  );
};

export default SupervisorHero;
